import React, { useState } from 'react'
import {
  Box,
  Button,
  FormHelperText,
  Grid,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import SearchIcon from '@mui/icons-material/Search'
import BaseDialog from '../dialogs/BaseDialog'
import DialogFooter from '../dialogs/DialogFooter'
import {
  AdminFormSection,
  FieldLabel,
  LabeledSelect,
  LabeledTextField,
  SegmentedSelector,
  type SegmentedOption,
} from '../../admin/shared/fields'
import { formFieldSurfaceSx } from '../../admin/shared/adminFormChrome.styles'
import { FreshdeskIcon } from '../../SystemIcons'
import type { Market, ReferenceLinkInput } from './types'
import { REFERENCE_LINK_TYPE_LABELS, referenceLinkTypeLabel } from './linkStyles'
import { isValidDraft, isValidUrl } from './validation'
import {
  extractArticleId,
  freshdeskMarketForUrl,
  freshdeskMarketMismatch,
  isFreshdeskUrl,
  normalizeFreshdeskUrl,
} from '../../../utils/freshdeskUrls'
import { useDataFetching } from '../../../hooks/useDataFetching'
import {
  FreshdeskSolutionArticle,
  integrationsApi,
} from '../../../services/admin/integrationsApi'
import { categoryFolder } from '../freshdesk/freshdeskArticleHelpers'
import SolutionsBrowserDialog from '../freshdesk/SolutionsBrowserDialog'

interface LinkFormDialogProps {
  open: boolean
  mode: 'add' | 'edit'
  draft: ReferenceLinkInput
  onDraftChange: (next: ReferenceLinkInput) => void
  allowedLinkTypes: string[]
  availableMarkets: readonly Market[]
  onSave: () => void
  onCancel: () => void
}

type LinkSource = 'solutions' | 'manual'

const SOURCE_OPTIONS: ReadonlyArray<SegmentedOption<LinkSource>> = [
  {
    label: 'Freshdesk Solutions article',
    value: 'solutions',
    icon: <FreshdeskIcon size={16} />,
  },
  { label: 'Manual URL', value: 'manual' },
]

/**
 * Add / edit a single reference link via the shared `BaseDialog` shell (the
 * canonical admin modal - see `FeedbackDetailDialog`). Fields live in an
 * `AdminFormSection card` panel, and `formFieldSurfaceSx` (passed as the
 * dialog's `contentSx`) whitens the inputs just like a page form's
 * `AdminFormCard` - so the inputs read white on the card, matching every other
 * admin edit form.
 *
 * The links array is owned by the parent form and saved inside the
 * exception-type PUT, so this dialog only mutates the in-flight `draft`;
 * `onSave` hands it back to `ReferenceLinksEditor`. Validation mirrors the old
 * inline row exactly.
 */
const LinkFormDialog: React.FC<LinkFormDialogProps> = ({
  open,
  mode,
  draft,
  onDraftChange,
  allowedLinkTypes,
  availableMarkets,
  onSave,
  onCancel,
}) => {
  // Source is a local modal affordance, independent of `link_type` and never
  // persisted: it only decides whether the URL is picked from the synced
  // Freshdesk Solutions catalog or typed by hand. The editor remounts this
  // dialog on every open (conditional render), so a useState initializer
  // derives the opening source from the draft URL once - an existing Freshdesk
  // article URL opens on the picker (the retain-guides path), everything else
  // (including a brand-new link) on Manual.
  const [source, setSource] = useState<LinkSource>(() =>
    isFreshdeskUrl(draft.url) ? 'solutions' : 'manual',
  )

  // The picker needs a concrete market (ANZ/USA). Prefer the chosen market;
  // fall back to the market encoded in a Freshdesk URL so an "All markets" link
  // to a market-specific article still loads + pre-selects. Never mutates the
  // stored market.
  const pickerMarket: Market | null =
    draft.market === 'ANZ' || draft.market === 'USA'
      ? draft.market
      : freshdeskMarketForUrl(draft.url)

  // The Freshdesk Solutions picker is only offered for Fix Guides; every other
  // link type is a plain manual URL. So the Source toggle (and the article picker)
  // appear only when the type is `fix_guide` - otherwise the free-text URL field
  // is the only option.
  const allowsSolutions = draft.link_type === 'fix_guide'
  const useSolutions = allowsSolutions && source === 'solutions'
  // Resolve the current Freshdesk URL to a synced article (market-scoped) so the
  // summary can show its title + Category > Folder. The browser dialog fetches the
  // full tree itself; this lighter call only drives the current-selection chip.
  const { data: articles } = useDataFetching<FreshdeskSolutionArticle[]>({
    fetchFn: () =>
      useSolutions && pickerMarket
        ? integrationsApi.getFreshdeskSolutionArticles(pickerMarket)
        : Promise.resolve([]),
    dependencies: [useSolutions, pickerMarket],
  })
  const [browserOpen, setBrowserOpen] = useState(false)

  const selectedArticle =
    (articles ?? []).find((a) => a.article_id === extractArticleId(draft.url)) ?? null

  const nameMissing = !draft.name.trim()
  const urlMissing = !draft.url.trim()
  const urlInvalid = !urlMissing && !isValidUrl(draft.url)
  // Non-blocking Freshdesk market check - only when the URL itself is valid so
  // it never competes with a hard error. Never gates the primary action.
  const marketMismatch =
    !urlMissing && !urlInvalid
      ? freshdeskMarketMismatch(draft.url, draft.market)
      : null

  const urlHelper = urlMissing
    ? 'URL is required'
    : urlInvalid
      ? 'URL must start with https://'
      : marketMismatch
        ? (
            <Box
              component="span"
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.5,
                // Colour the span itself (icon inherits it). A direct colour on
                // this child beats the grey helper-text colour the shared
                // `formFieldSurfaceSx` sets on the FormHelperText root, which a
                // per-field `FormHelperTextProps.sx` could not out-specify.
                color:
                  marketMismatch.kind === 'mismatch'
                    ? 'warning.main'
                    : 'text.secondary',
              }}
            >
              {marketMismatch.kind === 'mismatch' ? (
                <WarningAmberIcon sx={{ fontSize: '0.95rem', color: 'inherit' }} />
              ) : (
                <InfoOutlinedIcon sx={{ fontSize: '0.95rem', color: 'inherit' }} />
              )}
              {marketMismatch.message}
            </Box>
          )
        : undefined

  return (
    <BaseDialog
      open={open}
      onClose={onCancel}
      title={mode === 'add' ? 'Add link' : 'Edit link'}
      variant="edit"
      size="md"
      onSubmit={onSave}
      contentSx={formFieldSurfaceSx}
      footer={
        <DialogFooter
          cancel={{ onClick: onCancel }}
          primary={{
            label: mode === 'add' ? 'Add link' : 'Save changes',
            onClick: onSave,
            disabled: !isValidDraft(draft),
          }}
        />
      }
    >
      <Grid container spacing={2}>
        <AdminFormSection card title="Link details">
          <Grid container spacing={2.5}>
            <Grid item xs={12} sm={6}>
              <LabeledSelect
                label="Type"
                required
                value={draft.link_type}
                onChange={(e) =>
                  onDraftChange({ ...draft, link_type: String(e.target.value) })
                }
              >
                {allowedLinkTypes.map((t) => (
                  <MenuItem key={t} value={t}>
                    {REFERENCE_LINK_TYPE_LABELS[t] ?? referenceLinkTypeLabel(t)}
                  </MenuItem>
                ))}
              </LabeledSelect>
            </Grid>
            <Grid item xs={12} sm={6}>
              <LabeledSelect
                label="Market"
                value={draft.market ?? ''}
                onChange={(e) => {
                  const v = e.target.value
                  onDraftChange({
                    ...draft,
                    market: v === '' ? null : (v as Market),
                  })
                }}
              >
                <MenuItem value="">All markets</MenuItem>
                {availableMarkets.map((m) => (
                  <MenuItem key={m} value={m}>
                    {m}
                  </MenuItem>
                ))}
              </LabeledSelect>
            </Grid>
            {allowsSolutions && (
              <Grid item xs={12}>
                <FieldLabel>Source</FieldLabel>
                <SegmentedSelector
                  options={SOURCE_OPTIONS}
                  value={source}
                  onChange={setSource}
                  ariaLabel="link source"
                />
              </Grid>
            )}
            <Grid item xs={12}>
              {useSolutions ? (
                // Source = Freshdesk Solutions article: the URL is chosen via the
                // shared Solutions Browser (portal + category/folder tree + article
                // list) opened from the button below. The summary shows the current
                // pick; the browser writes the chosen article URL back into the draft.
                <>
                  <FieldLabel required>Article</FieldLabel>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.5,
                      p: 1.5,
                      border: 1,
                      borderColor: urlMissing ? 'error.main' : 'divider',
                      borderRadius: 1,
                    }}
                  >
                    <FreshdeskIcon size={20} />
                    <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                      {selectedArticle ? (
                        <>
                          {categoryFolder(
                            selectedArticle.category,
                            selectedArticle.folder,
                          )}
                          <Typography variant="body2" sx={{ fontWeight: 500 }} noWrap>
                            {selectedArticle.title}
                          </Typography>
                        </>
                      ) : isFreshdeskUrl(draft.url) ? (
                        // A Freshdesk URL we could not resolve to a synced article
                        // (other market, or not yet synced) - show the raw URL.
                        <Typography variant="body2" color="text.secondary" noWrap>
                          {draft.url}
                        </Typography>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          No article selected
                        </Typography>
                      )}
                    </Box>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<SearchIcon />}
                      onClick={() => setBrowserOpen(true)}
                    >
                      {selectedArticle || isFreshdeskUrl(draft.url)
                        ? 'Change article'
                        : 'Browse Solutions…'}
                    </Button>
                  </Box>
                  {urlMissing && (
                    <FormHelperText error sx={{ mx: 1.75 }}>
                      Pick an article
                    </FormHelperText>
                  )}
                  <SolutionsBrowserDialog
                    open={browserOpen}
                    onClose={() => setBrowserOpen(false)}
                    defaultMarket={pickerMarket ?? 'ANZ'}
                    selectedArticleId={selectedArticle?.article_id}
                    onSelect={(article) =>
                      onDraftChange({
                        ...draft,
                        url: article.url,
                        name: draft.name.trim() ? draft.name : article.title,
                      })
                    }
                  />
                </>
              ) : (
                <LabeledTextField
                  label="URL"
                  required
                  value={draft.url}
                  placeholder="https://help.poolwerx.com/..."
                  error={urlMissing || urlInvalid}
                  helperText={urlHelper}
                  onChange={(e) =>
                    onDraftChange({ ...draft, url: e.target.value })
                  }
                  onBlur={() => {
                    const normalized = normalizeFreshdeskUrl(draft.url)
                    if (normalized !== draft.url) {
                      onDraftChange({ ...draft, url: normalized })
                    }
                  }}
                />
              )}
            </Grid>
            <Grid item xs={12}>
              <LabeledTextField
                label="Name"
                required
                value={draft.name}
                placeholder="Fix Guide title"
                error={nameMissing}
                helperText={nameMissing ? 'Name is required' : undefined}
                onChange={(e) => onDraftChange({ ...draft, name: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <LabeledTextField
                label="Description"
                value={draft.description ?? ''}
                placeholder="Short description (optional) - what this link is"
                multiline
                rows={2}
                inputProps={{ maxLength: 500 }}
                onChange={(e) =>
                  onDraftChange({ ...draft, description: e.target.value })
                }
              />
            </Grid>
          </Grid>
        </AdminFormSection>
      </Grid>
    </BaseDialog>
  )
}

export default LinkFormDialog
